import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useCaseWebSocket from "@/hooks/useCaseWebSocket";
import CaseAccessLog from "@/components/CaseAccessLog";
import PermitChecklist from "@/components/PermitChecklist";
import CaseHeader from "@/components/case-detail/CaseHeader";
import CaseStatusBlock from "@/components/case-detail/CaseStatusBlock";
import CaseAIReview from "@/components/case-detail/CaseAIReview";
import CaseTimeline from "@/components/case-detail/CaseTimeline";
import CaseDocuments from "@/components/case-detail/CaseDocuments";
import CaseComments from "@/components/case-detail/CaseComments";
import CaseForwardModal from "@/components/case-detail/CaseForwardModal";
import CaseReviewMatrix from "@/components/case-detail/CaseReviewMatrix";
import CaseAtgardAnalys from "@/components/case-detail/CaseAtgardAnalys";
import CaseApplication from "@/components/case-detail/CaseApplication";
import { exportCasePdf } from "@/components/case-detail/caseExport";
import { STATUS } from "@/lib/utils-app";
import { toast } from "sonner";

export default function CaseDetail() {
  const { case_id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setCase] = useState(null);
  const [docs, setDocs] = useState([]);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [aiReview, setAiReview] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [c1, d1, m1] = await Promise.all([
        api.get(`/crm/cases/${case_id}`),
        api.get(`/crm/documents`, { params: { case_id } }),
        api.get(`/crm/comments`, { params: { case_id } }),
      ]);
      setCase(c1.data); setDocs(d1.data); setComments(m1.data);
    } catch (err) {
      console.error("Failed to load case", err);
      toast.error("Kunde inte hämta ärende");
      nav("/cases");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [case_id]);

  // Real-time comments via WebSocket
  useCaseWebSocket(case_id, (comment) => setComments((prev) => [...prev, comment]));

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post("/crm/comments", { case_id, text });
      setText("");
    } catch {
      toast.error("Kunde inte skicka kommentar");
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/crm/documents`, fd, { params: { case_id }, headers: { "Content-Type": "multipart/form-data" } });
      if (res.data?.auto_checklist_match) {
        toast.success(`Dokument uppladdat – auto-bockade "${res.data.auto_checklist_match.matched_text.slice(0, 45)}${res.data.auto_checklist_match.matched_text.length > 45 ? "…" : ""}" i checklistan`);
      } else {
        toast.success("Dokument uppladdat");
      }
      const [d1, c1] = await Promise.all([
        api.get(`/crm/documents`, { params: { case_id } }),
        api.get(`/crm/cases/${case_id}`),
      ]);
      setDocs(d1.data);
      setCase(c1.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Uppladdning misslyckades");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const changeStatus = async (newStatus) => {
    try {
      const { data } = await api.put(`/crm/cases/${case_id}`, { status: newStatus });
      setCase(data);
      toast.success(`Status: ${STATUS[newStatus]?.label || newStatus}`);
    } catch {
      toast.error("Kunde inte ändra status");
    }
  };

  const runAiReview = async () => {
    if (!c) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/project-review", {
        title: c.title, case_type: c.case_type,
        address: c.address, description: c.description,
      });
      setAiReview(data);
      toast.success("AI-granskning klar");
    } catch {
      toast.error("AI-granskning misslyckades");
    } finally {
      setAiLoading(false);
    }
  };

  const generateShare = async () => {
    try {
      const { data } = await api.post(`/crm/cases/${case_id}/share`);
      const url = `${window.location.origin}/shared/${data.share_token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Delningslänk kopierad till urklipp");
      } catch {
        toast.info(`Länk: ${url}`);
      }
    } catch {
      toast.error("Kunde inte skapa delningslänk");
    }
  };

  if (!c) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-zinc-500">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const canEditChecklist = user?.role === "admin" || c.owner_id === user?.user_id;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 animate-fade-in-up">
      <button data-testid="back-button" onClick={() => nav(-1)}
        className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 mb-6">
        <ArrowLeft className="w-4 h-4" /> Tillbaka
      </button>

      <div className="bg-white border border-zinc-200 rounded-sm p-6 lg:p-8">
        <CaseHeader
          c={c} user={user} aiLoading={aiLoading}
          onExportPdf={() => exportCasePdf(c)}
          onForward={() => setForwardOpen(true)}
          onAiReview={runAiReview}
          onShare={generateShare}
        />

        <CaseStatusBlock c={c} user={user} onChangeStatus={changeStatus} />

        {c.description && (
          <div className="mt-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Beskrivning</div>
            <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-line">{c.description}</p>
          </div>
        )}

        <CaseApplication application={c.application} />

        <CaseAIReview review={aiReview} />
      </div>

      {user?.role === "admin" && (
        <div className="mt-6 space-y-6">
          <CaseAtgardAnalys caseId={case_id} />
          <CaseReviewMatrix caseId={case_id} />
          <CaseAccessLog caseId={case_id} />
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <CaseTimeline events={c.timeline} />
        </div>

        <div className="lg:col-span-2 space-y-6">
          <PermitChecklist
            caseId={case_id}
            items={c.permit_checklist || []}
            meta={c.permit_meta}
            canEdit={canEditChecklist}
            onChange={(items) => setCase({ ...c, permit_checklist: items })}
          />
          <CaseDocuments docs={docs} uploading={uploading} onUpload={upload} fileRef={fileRef} />
          <CaseComments
            comments={comments} userId={user?.user_id}
            isAdmin={user?.role === "admin"} caseId={case_id}
            text={text} setText={setText} onSend={send}
          />
        </div>
      </div>

      {forwardOpen && (
        <CaseForwardModal
          caseRef={c.ref}
          caseTitle={c.title}
          docsCount={docs.length}
          onClose={() => setForwardOpen(false)}
          onSent={() => { setForwardOpen(false); load(); }}
        />
      )}
    </div>
  );
}
